import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { IoIosPeople } from 'react-icons/io';
import {
  MdAttachMoney,
  MdContentPaste,
  MdDashboard,
  MdEmail,
  MdFace,
  MdFormatListBulleted,
  MdHowToReg,
  MdOutlineLibraryBooks,
  MdPeopleOutline,
  MdPlace,
  MdSettings,
  MdSupervisorAccount,
  MdToday,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'redux-first-router-link';
import type { RootState } from 'src/root/store';

import ActivityIcon from '@assets/images/Activities.svg';
import { SHOW_COUNSELED_YOUTH, SHOW_DASHBOARD } from '@config/config';
import { pathnameSel } from '@location/duck/selectors';
import { tabNames } from '@modules/advancement';
import { navigateToTab } from '@modules/advancement/common';
import { setActiveAdvancementsTab } from '@modules/advancement/pendingItems';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { overrideShowUnitPaymentLogsSel } from '@modules/paymentLogs/duck/selectors';
import { hasRosterLiteAccessSel } from '@modules/roster-lite/duck/selectors';
import { hasRosterV2PermissionSel } from '@modules/roster-v2/duck/selectors';
import { SBL_4143_showMessagingSel } from '@modules/shared/duck/selectors/flags.selectors';
import { useActiveOrganization } from '@modules/unit/duck/hooks';
import { isUnitTypeSel } from '@modules/unit/duck/selectorsTyped';
import Menu from '@shared/components/Menu';
import { navigateToSettings } from '@shared/duck/actions';
import {
  childUserIdSel,
  isParentGuardianSel,
  newPermissionsSel,
} from '@shared/duck/selectors';
import {
  isMeritBadgeCounselorSel,
  isParentOrYouthMemberSel,
  isSelectedOrganizationValidSel,
  organizationGuidSel,
  shouldShowMBCSearchPageSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { unitInfoSel } from '@unit/duck/selectors';
import { userDataInitializedSel } from '@user/duck/selectors';

import { isReportsRouteSel, isSettingsRouteSel } from '../../duck/selectors';
import SettingsOptions from '../SettingsOptions';
import ForumMenuItem from './ForumMenuItem';
import styles from './Nav.less';
import NavMenuItem from './NavMenuItem';
import NavMenuItemContent from './NavMenuItemContent';
import ScoutbookLegacyMenuItem from './ScoutbookLegacyMenuItem';

const SubMenu = Menu.SubMenu;

interface NavProps {
  isExpanded: boolean;
  isMobile: boolean;
}

const Nav: React.FC<NavProps> = ({ isExpanded, isMobile }) => {
  const dispatch = useDispatch();

  const loading = !useSelector(userDataInitializedSel);
  const isSettingsRoute = useSelector(isSettingsRouteSel);
  const isReportsRoute = useSelector(isReportsRouteSel);
  const pathname = useSelector(pathnameSel);
  const isUnitType = useSelector(isUnitTypeSel);
  const isMeritBadgeCounselor = useSelector(isMeritBadgeCounselorSel);
  const activeOrganization = useActiveOrganization();
  const isInactiveUnit = activeOrganization.data?.charter?.isActive === false;
  const overrideShowUnitPaymentLogs = useSelector(
    overrideShowUnitPaymentLogsSel,
  );
  const newUserPermissions = useSelector(newPermissionsSel);
  const userPermissions = newUserPermissions;
  const isSelectedUnitValid = useSelector(isSelectedOrganizationValidSel);
  const organizationGuid = useSelector(organizationGuidSel);
  const hasRosterLiteAccess = useSelector(hasRosterLiteAccessSel);
  const isRosterLite =
    featureFlags.getFlag('SBL_6750_ROSTER_LITE') && hasRosterLiteAccess;
  const rosterLitePath = `/roster-lite/${organizationGuid}`;
  const hasRosterV2Permission = useSelector(hasRosterV2PermissionSel);
  const showRosterV2 =
    featureFlags.getFlag('SBL_6888_ROSTER_V2') && hasRosterV2Permission;
  const rosterV2Path = `/roster-v2/${organizationGuid}`;
  const isParentOrYouthMember = useSelector(isParentOrYouthMemberSel);
  const showUnitRoster =
    featureFlags.getFlag('SBL_6895_UNIT_ROSTER') && isParentOrYouthMember;
  const unitRosterPath = `/unit-roster/${organizationGuid}`;
  const showMessaging = useSelector(SBL_4143_showMessagingSel);
  const isParentGuardian = useSelector(isParentGuardianSel);
  const childUserId = useSelector(childUserIdSel);
  const shouldShowMBCSearchPage = useSelector(shouldShowMBCSearchPageSel);
  const showMbcMessaging = featureFlags.getFlag('SBL_4409_SHOW_MBC_MESSAGING');

  const [subMenuExpanded, setSubMenuExpanded] = useState<string | null>(
    isSettingsRoute ? pathname : null,
  );
  const [activeOption, setActiveOption] = useState(pathname);

  const shouldShowCalendar = useSelector((state: RootState) => {
    const newPermission = newPermissionsSel(state);
    const canViewCalendarPage = newPermission.VIEW_CALENDAR_PAGE;
    const isParentGuardian = isParentGuardianSel(state);
    const unitInfo = unitInfoSel(state);

    if (isParentGuardian || canViewCalendarPage) {
      return true;
    }

    if (!unitInfo) return false;

    return true;
  });

  useEffect(() => {
    if (isSettingsRoute) {
      setSubMenuExpanded('/settings');
      setActiveOption('/settings');
    } else if (isReportsRoute) {
      setActiveOption('/reports');
    } else {
      setActiveOption(pathname);
    }
  }, [isSettingsRoute, isReportsRoute, pathname]);

  const hasPerms = useCallback(
    permission => {
      const permArray = Array.isArray(permission) ? permission : [permission];
      return permArray.some(perm => userPermissions[perm]);
    },
    [userPermissions],
  );

  const shouldEnableRoster = () =>
    userPermissions[newPermissions.VIEW_ROSTER_PAGE] && isSelectedUnitValid;

  const shouldShowRoster = () =>
    userPermissions[newPermissions.VIEW_ROSTER_PAGE] ??
    userPermissions[newPermissions.VIEW_COUNCIL_UNITS_PAGE];

  const shouldShowUnitPaymentLogs = () =>
    (featureFlags.getFlag('SHOW_UNIT_PAYMENT_LOGS') ||
      overrideShowUnitPaymentLogs) &&
    isUnitType &&
    userPermissions[newPermissions.CRUD_UNIT_LOG];

  const shouldEnablePermissionsManager = () =>
    userPermissions[newPermissions.VIEW_PERMISSIONS_MANAGER] &&
    isSelectedUnitValid;

  const shouldShowMeritBadgeCounselors = () => shouldShowMBCSearchPage;

  const shouldShowMBCDashboard = () =>
    featureFlags.getFlag('SBL_4405_SHOW_MBC_DASHBOARD') &&
    userPermissions[newPermissions.VIEW_MBC_DASHBOARD];

  const handleMenuClick = useCallback(
    ({
      item,
      key,
    }: {
      item: { props: { selectable: boolean } };
      key: string;
    }) => {
      const { selectable = true } = item.props;

      if (key === '/roster') {
        dispatch(setActiveAdvancementsTab(null));
        dispatch(navigateToTab(tabNames.ROSTER));
      }

      if (selectable) {
        if (subMenuExpanded && !key.includes(subMenuExpanded)) {
          setSubMenuExpanded(null);
        }

        setActiveOption(key);
      }
    },
    [dispatch, subMenuExpanded],
  );

  // key of title
  const handleSubMenuExpand = ({ key }: { key: string; domEvent: Event }) => {
    if (key === '/settings') {
      dispatch(navigateToSettings());
    }
    setSubMenuExpanded(key);
    setActiveOption(key);
  };

  const openSubMenuKeys = useMemo(
    () => (subMenuExpanded ? [subMenuExpanded] : []),
    [subMenuExpanded],
  );
  const showLabels = isMobile || isExpanded;
  const showRoster = shouldShowRoster();
  const youthProfileLink =
    isParentGuardian && childUserId ? `/youthProfile/${childUserId}` : '';

  const reportMenu = (
    <NavMenuItem
      key="/reports"
      disabled={loading || !userPermissions[newPermissions.VIEW_REPORTS_PAGE]}
    >
      <NavLink to="/reports" id="qa_reports">
        <NavMenuItemContent
          icon={<MdContentPaste />}
          showLabels={showLabels}
          loading={loading}
        >
          <FormattedMessage id="page.reports" />
        </NavMenuItemContent>
      </NavLink>
    </NavMenuItem>
  );

  const notCalendarPermissions =
    !userPermissions[newPermissions.VIEW_CALENDAR_PAGE];

  const disableCalendar =
    loading || isInactiveUnit || notCalendarPermissions || !isUnitType;
  const disableMBCMessaging =
    !isUnitType && (!showMbcMessaging || !isMeritBadgeCounselor);

  return (
    <Menu
      mode="inline"
      className={cn(styles.menu, {
        [styles.subMenuActive]: subMenuExpanded,
      })}
      defaultSelectedKeys={[pathname]}
      openKeys={openSubMenuKeys}
      selectedKeys={[activeOption]}
      onClick={handleMenuClick}
    >
      {hasPerms(newPermissions.VIEW_COUNCIL_UNITS_PAGE) && (
        <NavMenuItem
          key="/units"
          inlineCollapsed={!showLabels}
          disabled={loading}
        >
          <NavLink to="/units">
            <NavMenuItemContent
              icon={<MdPlace />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.units" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!SHOW_DASHBOARD && (
        <NavMenuItem key="/" inlineCollapsed={!showLabels} disabled={loading}>
          <NavLink to="/">
            <NavMenuItemContent
              icon={<MdDashboard />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.dashboard" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!showRoster && (
        <NavMenuItem key="/roster" disabled={loading || !shouldEnableRoster()}>
          <NavLink to="/roster" id="qa_roster">
            <NavMenuItemContent
              icon={<MdFormatListBulleted />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.roster" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!isRosterLite && (
        <NavMenuItem
          key={rosterLitePath}
          disabled={loading || !isSelectedUnitValid}
        >
          <NavLink to={rosterLitePath} id="qa_roster_lite">
            <NavMenuItemContent
              icon={<MdFormatListBulleted />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.roster" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!showRosterV2 && (
        <NavMenuItem
          key={rosterV2Path}
          disabled={loading || !isSelectedUnitValid}
        >
          <NavLink to={rosterV2Path} id="qa_roster_v2">
            <NavMenuItemContent
              icon={<MdFormatListBulleted />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.rosterV2" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!showUnitRoster && (
        <NavMenuItem
          key={unitRosterPath}
          disabled={loading || !isSelectedUnitValid}
        >
          <NavLink to={unitRosterPath} id="qa_unit_roster">
            <NavMenuItemContent
              icon={<MdFormatListBulleted />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.unitRoster" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!shouldShowCalendar && hasPerms(newPermissions.VIEW_CALENDAR_PAGE) && (
        <NavMenuItem key="/calendar" disabled={disableCalendar}>
          <NavLink to="/calendar">
            <NavMenuItemContent
              icon={<MdToday />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.calendar" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!(showMessaging && hasPerms(newPermissions.VIEW_MESSAGING)) && (
        <NavMenuItem
          disabled={loading || isInactiveUnit || disableMBCMessaging}
          key="/messaging"
        >
          <NavLink
            to={isMeritBadgeCounselor ? '/mbc-messaging' : '/messaging'}
            id="qa_messaging"
          >
            <NavMenuItemContent
              icon={<MdEmail />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.messaging" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {shouldShowMeritBadgeCounselors() && (
        <NavMenuItem disabled={isInactiveUnit} key="/meritBadgeCounselors">
          <NavLink to="/meritBadgeCounselors" id="qa_meritBadgeCounselors">
            <NavMenuItemContent
              icon={<MdHowToReg />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="core.Nav.meritBadgeCounselors" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {shouldShowMBCDashboard() ? (
        <NavMenuItem key="/meritBadgeCounselors/dashboard">
          <NavLink to="/meritBadgeCounselors/dashboard" id="qa_mbc_dashboard">
            <NavMenuItemContent
              icon={<IoIosPeople />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.mbcDashboard" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      ) : null}
      {!!(
        SHOW_COUNSELED_YOUTH && hasPerms(newPermissions.VIEW_COUNSELED_YOUTH)
      ) && (
        <NavMenuItem key="/counseledYouth">
          <NavLink to="/counseledYouth" id="qa_counseledYouth">
            <NavMenuItemContent
              icon={<MdSupervisorAccount />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.counseledYouth" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!isParentGuardian && (
        <NavMenuItem
          key={youthProfileLink}
          disabled={
            loading || !userPermissions[newPermissions.VIEW_PROFILE_PAGE]
          }
        >
          <NavLink to={youthProfileLink} id="qa_youth_profile">
            <NavMenuItemContent
              icon={<MdFace />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.youthProfile" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {shouldShowUnitPaymentLogs() && (
        <NavMenuItem
          disabled={loading || isInactiveUnit}
          key="/unitPaymentLogs"
        >
          <NavLink id="unit_payment_logs" to="/unitPaymentLogs">
            <NavMenuItemContent
              icon={<MdAttachMoney />}
              loading={loading}
              showLabels={showLabels}
            >
              <FormattedMessage id="core.Nav.unitPaymentLogs" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      <NavMenuItem
        key="/profile"
        disabled={loading || !userPermissions[newPermissions.VIEW_PROFILE_PAGE]}
      >
        <NavLink to="/profile" id="qa_profile">
          <NavMenuItemContent
            icon={<MdFace />}
            showLabels={showLabels}
            loading={loading}
          >
            <FormattedMessage id="page.profile" />
          </NavMenuItemContent>
        </NavLink>
      </NavMenuItem>
      {hasPerms([
        newPermissions.VIEW_ROSTER_PAGE,
        newPermissions.VIEW_REPORTS_PAGE,
      ]) && reportMenu}
      {hasPerms([newPermissions.VIEW_ACTIVITY_PAGE]) && (
        <NavMenuItem
          key="/activities"
          disabled={loading || isInactiveUnit || !isSelectedUnitValid}
        >
          <NavLink to="/activities" id="qa_activities">
            <NavMenuItemContent
              icon={<ActivityIcon />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.activities" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {hasPerms(newPermissions.SHOW_FORUM_LINK) && (
        <ForumMenuItem showLabels={showLabels} loading={loading} />
      )}
      {hasPerms(newPermissions.SHOW_SCOUTBOOK_LINK) && (
        <ScoutbookLegacyMenuItem showLabels={showLabels} loading={loading} />
      )}
      {!!hasPerms(newPermissions.VIEW_PERMISSIONS_MANAGER) && (
        <NavMenuItem
          key="/permissionsManager"
          disabled={
            loading || isInactiveUnit || !shouldEnablePermissionsManager()
          }
        >
          <NavLink to="/permissionsManager" id="qa_permissionsManager">
            <NavMenuItemContent
              icon={<MdPeopleOutline />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.permissionsManager" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}

      <NavMenuItem key="/references">
        <NavLink to="/references" id="qa_references">
          <NavMenuItemContent
            icon={<MdOutlineLibraryBooks />}
            showLabels={showLabels}
            loading={loading}
          >
            <FormattedMessage id="page.references" />
          </NavMenuItemContent>
        </NavLink>
      </NavMenuItem>

      {isMobile ? (
        <SubMenu
          key="/settings"
          className={cn(
            styles.navOption,
            styles.navSection,
            styles.navSubMenu,
            styles.settingsMenu,
            {
              [styles.alwaysActive]: isSettingsRoute,
              Joyride__SettingsMenu: isMobile,
            },
          )}
          title={
            <NavMenuItemContent
              icon={<MdSettings />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="core.Nav.settingsMenu" />
            </NavMenuItemContent>
          }
          disabled={loading}
          onTitleClick={handleSubMenuExpand}
        >
          <SettingsOptions isMobile={true} onClick={handleMenuClick} />
        </SubMenu>
      ) : (
        <NavMenuItem
          key="/settings"
          disabled={loading}
          className={cn(styles.settings)}
        >
          <NavLink to="/settings" id="qa_settings">
            <NavMenuItemContent
              icon={<MdSettings />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="core.Header.Settings.settings" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
    </Menu>
  );
};

export default Nav;
